import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Sparkle, Plus, Trash, ArrowSquareOut } from "@phosphor-icons/react";
import { toast } from "sonner";

const CATEGORIES = [
  "Business Setup", "International Expansion", "Tax & Compliance",
  "Business Opportunities", "Import Export", "Startup Growth",
  "Market Research", "Immigration Updates",
];

export default function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [presets, setPresets] = useState([]);
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("preset"); // preset | custom
  const [presetId, setPresetId] = useState("");
  const [topic, setTopic] = useState("");
  const [category, setCategory] = useState("Business Setup");
  const [busy, setBusy] = useState(false);

  const refresh = () => api.get("/admin/blog").then((r) => setPosts(r.data));
  useEffect(() => {
    refresh();
    api.get("/admin/blog/presets").then((r) => setPresets(r.data));
  }, []);

  const generate = async () => {
    setBusy(true);
    try {
      const body = mode === "preset" ? { preset_id: presetId } : { topic, category };
      const { data } = await api.post("/admin/blog/generate", body);
      toast.success(`Published "${data.title}"`);
      setOpen(false);
      setPresetId(""); setTopic("");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generation failed");
    } finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    await api.delete(`/admin/blog/${id}`);
    refresh();
  };

  return (
    <div data-testid="admin-blog">
      <div className="flex justify-between items-start mb-6">
        <div>
          <div className="overline mb-2">Blog</div>
          <h2 className="font-display text-2xl text-gos-charcoal">Editorial · {posts.length} posts</h2>
          <p className="text-sm text-gos-muted mt-2 max-w-xl">Generate medium-length, AI-written posts with an AI cover image. Auto-publishes on creation — edit or delete from this list.</p>
        </div>
        <button onClick={() => setOpen(true)} data-testid="blog-generate-open" className="btn-sand text-xs !py-2">
          <Sparkle size={14} weight="duotone" /> Generate post
        </button>
      </div>

      <div className="bg-white border border-gos-border rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gos-subtle text-xs uppercase tracking-widest text-gos-navy">
            <tr><th className="text-left p-3">Title</th><th className="text-left p-3">Category</th><th className="text-left p-3">Source</th><th className="text-left p-3">Created</th><th className="text-left p-3"></th></tr>
          </thead>
          <tbody>
            {posts.map((p) => (
              <tr key={p.id} data-testid={`blog-row-${p.id}`} className="border-t border-gos-border">
                <td className="p-3">
                  <div className="text-gos-charcoal">{p.title}</div>
                  <div className="text-xs text-gos-muted">{p.read_minutes} min · {p.published ? "Published" : "Draft"}</div>
                </td>
                <td className="p-3 text-xs text-gos-slate">{p.category}</td>
                <td className="p-3 text-xs">
                  <span className={`px-2 py-0.5 uppercase tracking-widest text-[10px] ${p.ai_generated ? "bg-gos-subtle text-gos-sand" : "bg-gos-subtle text-gos-slate"}`}>
                    {p.ai_generated ? "AI" : "Manual"}
                  </span>
                </td>
                <td className="p-3 text-xs text-gos-muted">{p.created_at?.slice(0, 10)}</td>
                <td className="p-3 flex gap-3">
                  <Link to={`/blog/${p.slug}`} target="_blank" data-testid={`blog-view-${p.id}`} className="text-gos-navy hover:text-gos-sand"><ArrowSquareOut size={16} /></Link>
                  <button onClick={() => del(p.id)} data-testid={`blog-del-${p.id}`} className="text-gos-error hover:text-gos-navyHover"><Trash size={16} /></button>
                </td>
              </tr>
            ))}
            {posts.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-gos-muted">No posts yet — click "Generate post" to publish one.</td></tr>}
          </tbody>
        </table>
      </div>

      {open && (
        <div data-testid="blog-generate-modal" className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6">
          <div className="bg-white rounded-sm border border-gos-border max-w-lg w-full p-6">
            <div className="overline mb-2">AI blog generator</div>
            <h3 className="font-display text-2xl text-gos-navy mb-4">Generate a new post</h3>

            <div className="flex gap-2 mb-5">
              <button onClick={() => setMode("preset")} data-testid="blog-mode-preset"
                className={`flex-1 px-3 py-2 text-xs uppercase tracking-widest border ${mode === "preset" ? "bg-gos-navy text-white border-gos-navy" : "bg-white border-gos-border text-gos-slate"}`}>Preset topic</button>
              <button onClick={() => setMode("custom")} data-testid="blog-mode-custom"
                className={`flex-1 px-3 py-2 text-xs uppercase tracking-widest border ${mode === "custom" ? "bg-gos-navy text-white border-gos-navy" : "bg-white border-gos-border text-gos-slate"}`}>Custom topic</button>
            </div>

            {mode === "preset" ? (
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-gos-muted mb-1">Choose a preset</label>
                <select data-testid="blog-preset-select" value={presetId} onChange={(e) => setPresetId(e.target.value)}
                  className="w-full border border-gos-border rounded-sm px-2 py-2 text-sm bg-white">
                  <option value="">— Select a topic —</option>
                  {presets.map((p) => (
                    <option key={p.id} value={p.id}>[{p.category}] {p.topic}</option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-gos-muted mb-1">Topic</label>
                  <textarea data-testid="blog-custom-topic" value={topic} onChange={(e) => setTopic(e.target.value)} rows={3}
                    placeholder="e.g. How fintech startups should structure entry into the GCC in 2026"
                    className="w-full border border-gos-border rounded-sm px-2 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-gos-muted mb-1">Category</label>
                  <select data-testid="blog-custom-category" value={category} onChange={(e) => setCategory(e.target.value)}
                    className="w-full border border-gos-border rounded-sm px-2 py-2 text-sm bg-white">
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
            )}

            <div className="mt-5 flex gap-2 justify-end">
              <button onClick={() => setOpen(false)} className="btn-secondary text-xs !py-2">Cancel</button>
              <button onClick={generate} disabled={busy || (mode === "preset" ? !presetId : !topic.trim())}
                data-testid="blog-generate-btn" className="btn-primary text-xs !py-2 disabled:opacity-50">
                <Sparkle size={14} weight="duotone" /> {busy ? "Writing…" : "Generate & Publish"}
              </button>
            </div>
            <p className="text-[10px] text-gos-muted mt-4">Claude writes ~700 words with an AI-generated cover image. Total ~10–20 seconds.</p>
          </div>
        </div>
      )}
    </div>
  );
}
